import { AlarmClock, MoreVertical } from "lucide-react";
import { useTranslate } from "ra-core";
import type { ReactEventHandler } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const MENU_ITEM_CLASS =
  "cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm";

/**
 * The controls at the right edge of a task row.
 *
 * Postponing to tomorrow is a daily action, so it is one click here rather than
 * three levels down the menu. It is revealed on hover so that fifty rows do not
 * read as fifty buttons, and stays visible where there is no hover to reveal it
 * -- touch, and keyboard focus.
 */
export const TaskRowActions = ({
  onPostponeTomorrow,
  onPostponeNextWeek,
  onPostponeNextMonday,
  onEdit,
  onDelete,
}: {
  onPostponeTomorrow: ReactEventHandler;
  onPostponeNextWeek: ReactEventHandler;
  onPostponeNextMonday: ReactEventHandler;
  /** Omitted where the surface deliberately offers no editing -- see AccountTasks. */
  onEdit?: ReactEventHandler;
  /**
   * `useDeleteWithUndoController` hands back an event handler, not a thunk.
   * Omitted where deleting from that surface would be too destructive to offer.
   */
  onDelete?: ReactEventHandler;
}) => {
  const translate = useTranslate();

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="size-8 cursor-pointer opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 max-md:opacity-100 md:size-7"
        aria-label={translate("resources.tasks.actions.postpone_tomorrow")}
        onClick={onPostponeTomorrow}
      >
        <AlarmClock className="size-5 md:size-4" />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 cursor-pointer md:size-7"
            aria-label={translate("resources.tasks.actions.title")}
          >
            <MoreVertical className="size-5 md:size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className={MENU_ITEM_CLASS}
            onClick={onPostponeTomorrow}
          >
            {translate("resources.tasks.actions.postpone_tomorrow")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className={MENU_ITEM_CLASS}
            onClick={onPostponeNextWeek}
          >
            {translate("resources.tasks.actions.postpone_next_week")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className={MENU_ITEM_CLASS}
            onClick={onPostponeNextMonday}
          >
            {translate("resources.tasks.actions.postpone_next_monday")}
          </DropdownMenuItem>
          {onEdit && (
            <DropdownMenuItem className={MENU_ITEM_CLASS} onClick={onEdit}>
              {translate("ra.action.edit")}
            </DropdownMenuItem>
          )}
          {onDelete && (
            <DropdownMenuItem className={MENU_ITEM_CLASS} onClick={onDelete}>
              {translate("ra.action.delete")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
